import { useEffect, useRef } from 'react'
import { isEqual } from 'lodash'
import { dataIdFromObject } from '@sketch/graphql-cache'

import { isTruthy, useOnEvent } from '@sketch/utils'

import { handleFetchMore } from '@sketch/components'

import { useSortingSettings } from '../../../shares/hooks/useSortSettings'

import { useGetStorageItemsQuery } from '@sketch/gql-types'

import { StorageItemFilter } from '../../../storageItems/hooks'

// If StorageItemFilterType enum will be expanded by BE, TypeScript still should pass
// therefore we can ignore this warning
// eslint-disable-next-line no-restricted-imports
import {
  ShareSortOrder,
  StorageItemFilterType,
  StorageItemType,
} from '@sketch/gql-types/expansive'

export interface GetStorageItemsProps {
  workspaceId: string
  projectId: string
  search?: string
  filters?: StorageItemFilter
  types?: StorageItemType[]
}

const storageItemsEntriesPath = ['project', 'storageItems', 'entries']

export const parseFilters = (
  filtersObj: StorageItemFilter | undefined,
  search: string | undefined
): {
  filters: StorageItemFilterType[] | undefined
  includeAllNestedLevels: boolean
} => {
  const includeAllNestedLevels = search
    ? !filtersObj?.searchIgnoringNestedFolders
    : false

  if (!filtersObj?.documents) {
    return { filters: undefined, includeAllNestedLevels }
  }

  const filtersRaw: (StorageItemFilterType | false)[] = [
    filtersObj.documents.userCanEdit && 'I_CAN_EDIT',
    filtersObj.documents.withPrototypes && 'HAS_PROTOTYPES',
  ]
  const filters = filtersRaw.filter(isTruthy)

  return { filters, includeAllNestedLevels }
}

export const normalizePropsToVariables = (
  props: GetStorageItemsProps,
  sort: ShareSortOrder
) => {
  const { projectId, search, filters, types } = props

  return {
    projectIdentifier: projectId,
    search,
    ...parseFilters(filters, search),
    after: null,
    sharesSortOrder: sort,
    types,
  }
}

export const useGetStorageItems = (props: GetStorageItemsProps) => {
  const { workspaceId, projectId } = props
  const { sort } = useSortingSettings()
  const previousTotalCountsRef = useRef({
    storageItems: 0,
    shares: 0,
    projects: 0,
  })

  // Reset cached counts when switching projects to avoid showing stale data
  useEffect(() => {
    previousTotalCountsRef.current = {
      storageItems: 0,
      shares: 0,
      projects: 0,
    }
  }, [projectId])

  const { data, fetchMore, refetch, ...rest } = useGetStorageItemsQuery({
    variables: normalizePropsToVariables(props, sort),
    // This fetch policy is needed to make sure the nested projects are shown
    // correctly in the project view after restoring a project from trash
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.projectIdentifier !== curr?.projectIdentifier ||
      !isEqual(prev?.types, curr?.types),
  })

  useOnEvent(
    'workspaceShareRefresh',
    ({ workspaceIdentifier, projectIdentifier }) => {
      // Not the workspace visible ignore then
      if (
        workspaceIdentifier !== workspaceId ||
        projectIdentifier !== projectId
      ) {
        return
      }

      refetch()
    }
  )

  useOnEvent('storageItemTransferred', ({ location }) => {
    const { destination } = location

    // Not the workspace visible ignore then
    if (
      !destination.projectId ||
      destination.projectId !== projectId ||
      destination.workspaceId !== workspaceId
    ) {
      return
    }

    refetch()
  })

  useOnEvent('nestedProjectCreated', ({ parentProjectId }) => {
    if (parentProjectId !== projectId) return

    refetch()
  })

  const afterProjectItems = data?.project?.storageItems.meta.after ?? null

  const fetchMoreFilled = handleFetchMore(fetchMore, storageItemsEntriesPath, {
    dataIdFromObject,
    after: afterProjectItems,
  })
  const storageItems = data?.project?.storageItems.entries

  const meta = data?.project?.storageItems?.meta

  // Preserve previous totalCounts values while loading to avoid resetting to 0
  const totalCounts = data?.project
    ? {
        storageItems: meta?.totalCount ?? 0,
        shares: meta?.sharesCount ?? 0,
        projects: meta?.nestedProjectsCount ?? 0,
      }
    : previousTotalCountsRef.current

  // Update ref when data is available
  if (data?.project) {
    previousTotalCountsRef.current = totalCounts
  }

  return {
    ...rest,
    storageItems,
    fetchMore: fetchMoreFilled,
    data,
    totalCounts,
  }
}
